import { Component, ErrorInfo, ReactNode } from "react";

import { reportClientError } from "../utils/reportClientError";
import { descreverErro } from "../utils/descreverErro";

/**
 * Última linha de defesa contra crash de render.
 *
 * Por que existe: em 2026-07-28 o widget carregou e SUMIU para todo cliente por ~1h20. Uma exceção
 * no render (`mime-types.lookup` tocando `path`, sem polyfill no webpack) derrubou a árvore React
 * inteira. Passou por suíte verde, typecheck limpo e build OK — os três rodam em Node, nenhum é o
 * navegador. E a **detecção foi humana**: relato no WhatsApp, ~1h20 depois.
 *
 * O `reportClientError` cobre falha de ENVIO e de abrir sessão; erro de render acontece antes de
 * qualquer `catch` nosso. Sem este boundary, o modo de falha mais grave do widget é justamente o
 * único que não avisa ninguém.
 *
 * 🔴 O fallback NÃO usa Paste, Redux ou qualquer coisa do bundle que possa ter quebrado — HTML puro
 * com estilo inline. Um fallback que dependa do que caiu cai junto, e o cliente volta à tela branca.
 */
interface Props {
    children: ReactNode;
}

interface State {
    quebrou: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
    state: State = { quebrou: false };

    /** Já reportou este crash? Evita uma mensagem por re-render numa árvore em loop. */
    private jaReportou = false;

    static getDerivedStateFromError(): State {
        return { quebrou: true };
    }

    componentDidCatch(error: Error, info: ErrorInfo): void {
        if (this.jaReportou) {
            return;
        }
        this.jaReportou = true;
        /*
         * `componentStack` diz QUAL componente quebrou — é o que aponta o arquivo. Vai junto do
         * motivo, cortado, porque sem ele o alerta diz "quebrou" e não "quebrou onde".
         * Sem PII: é nome de componente nosso, não dado de cliente.
         */
        const ondeQuebrou = (info?.componentStack ?? "")
            .split("\n")
            .map((linha) =>
                linha
                    .trim()
                    // "at Componente (http://host/static/js/main.js:1:2)" → "Componente".
                    // A URL do bundle é sempre a mesma e comia o orçamento de 200 chars do motivo.
                    .replace(/^at\s+/, "")
                    .replace(/\s*\(.*$/, "")
            )
            .filter(Boolean)
            .slice(0, 3)
            .join(" < ");
        reportClientError("render_crashed", {
            reason: `${descreverErro(error)}${ondeQuebrou ? ` em ${ondeQuebrou}` : ""}`
        });
    }

    render(): ReactNode {
        if (!this.state.quebrou) {
            return this.props.children;
        }
        return (
            <div
                data-test="error-boundary-fallback"
                role="alert"
                style={{
                    padding: "16px",
                    font: "14px/1.4 system-ui, -apple-system, sans-serif",
                    color: "#1f2937",
                    background: "#fff",
                    textAlign: "center"
                }}
            >
                <p style={{ margin: "0 0 12px" }}>Não foi possível carregar o atendimento.</p>
                <button
                    type="button"
                    onClick={() => window.location.reload()}
                    style={{
                        padding: "8px 16px",
                        border: "1px solid #d1d5db",
                        borderRadius: "4px",
                        background: "#f9fafb",
                        cursor: "pointer",
                        font: "inherit"
                    }}
                >
                    Recarregue a página
                </button>
            </div>
        );
    }
}
